import { Injectable } from '@angular/core';
import { Subscription, BehaviorSubject, throwError, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

// INTERFACES
import { IBinLocation, ILocation } from '../../../../location/interfaces';
import { IKeyValue } from '../../../interfaces/common.interface';

// SERVICES
import { EnvironmentUtilService } from '../../util/environment-util.service';
import { ApiService } from '../api.service';

@Injectable({
  providedIn: 'root',
})
export class LocationApiService {
  private readonly _locations = new BehaviorSubject<any>(<any>{});
  private readonly _warehouse = new BehaviorSubject<any>(<any>null);
  private readonly _locationsByRfqId = new BehaviorSubject<{
    [key :string] :ILocation[];
  }>(<{ [key :string] :ILocation[] }>{});
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _currentTempLocation = new BehaviorSubject<ILocation>(
    <ILocation>{},
  );
  private readonly _currentTempLocationLoading = new BehaviorSubject<boolean>(
    <boolean>false,
  );
  private readonly _countries = new BehaviorSubject<IKeyValue[]>(<IKeyValue[]>[]);
  private readonly _binLocations = new BehaviorSubject<IBinLocation[]>(
    <IBinLocation[]>[],
  );

  private readonly _selectedLocations = new BehaviorSubject<any[]>(<any[]>[]);
  private readonly _selectedSkuLocations = new BehaviorSubject<any[]>(<any[]>[]);

  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  getById(id :number) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV1}/locations/${id}`);
  }

  getRfqLocations(data :{
    id :number;
    params ?:{ [param :string] :string | string[] };
  }) :Subscription {
    const { id, params = {} } = data;

    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/rfqs/${id}/locations`, { params })
      .subscribe({
        next: (response :any) => {
          this._locationsByRfqId.next({
            ...this._locationsByRfqId.value,
            [id]: response.data,
          });
          this._loading.next(false);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  getCountries() :Subscription {
    return this.apiService
      .get(`${this.environment.urlV2}/manage/countries`)
      .subscribe({
        next: (countries :any) => {
          this._countries.next(countries);

          return countries;
        },
        error: (error) => {
          this._currentTempLocationLoading.next(false);

          return throwError(() => new Error(error));
        },
      });
  }

  getTempLocation(data :{
    id :number;
    params ?:{ [param :string] :string | string[] };
  }) :Subscription {
    const { id, params } = data;

    this._currentTempLocationLoading.next(true);

    return this.apiService
      .get(`${this.environment.urlV1}/temp-location/${id}`, { params })
      .subscribe({
        next: (response :any) => {
          this._currentTempLocation.next(response);
          this._currentTempLocationLoading.next(false);

          return response;
        },
        error: (error) => {
          this._currentTempLocationLoading.next(false);

          return throwError(() => new Error(error));
        },
      });
  }

  getAll(params :any) :Subscription {
    return this.apiService
      .get(`${this.environment.urlV1}/locations`, { params })
      .subscribe({
        next: (response :IBinLocation[]) => {
          this._binLocations.next(response);
          this._loading.next(false);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  getAllV2(params :any) :Observable<any> {
    return this.apiService
      .get(`${this.environment.urlV1}/locations`, {
        params,
      })
      .pipe(
        map((response :any) => {
          return response.data;
        }),
      );
  }

  getWarehouseById(id :number) :Subscription {
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV1}/locations/${id}`)
      .subscribe({
        next: (response :any) => {
          this._loading.next(false);
          this._warehouse.next(response);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  transferSkuLocation(data :any) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/inventory/sku-locations/bulk-transfer`,
      body: data,
    });
  }

  setSelectedLocations(locations :any) :void {
    this._selectedLocations.next(locations);
  }
  setSelectedSkuLocations(data :any) :void {
    this._selectedSkuLocations.next(data);
  }

  get(params ?:{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV1}/locations`, {
      params,
    });
  }

  get locations$() :Observable<any> {
    return this._locations.asObservable();
  }

  get warehouse$() :Observable<any> {
    return this._warehouse.asObservable();
  }

  get locationsByRfqId$() :Observable<{
    [key :string] :ILocation[];
  }> {
    return this._locationsByRfqId.asObservable();
  }

  get loading$() :Observable<boolean> {
    return this._loading.asObservable();
  }

  get currentTempLocation$() :Observable<ILocation> {
    return this._currentTempLocation.asObservable();
  }

  get currentTempLocationLoading$() :Observable<boolean> {
    return this._currentTempLocationLoading.asObservable();
  }

  get countries$() :Observable<IKeyValue[]> {
    return this._countries.asObservable();
  }

  get binLocations$() :Observable<IBinLocation[]> {
    return this._binLocations.asObservable();
  }

  get selectedLocations$() :Observable<any[]> {
    return this._selectedLocations.asObservable();
  }

  get selectedSkuLocations$() :Observable<any[]> {
    return this._selectedSkuLocations.asObservable();
  }

  getRfqLocationsV2(data :{
    id :number;
    params ?:{ [param :string] :string | string[] };
  }) :Observable<any> {
    const { id, params = {} } = data;

    return this.apiService.get(`${this.environment.urlV2}/rfqs/${id}/locations`, {
      params,
    });
  }

  getLocationsByParentId(params ?:{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV1}/locations`, {
      params,
    });
  }

  getByIdV2(data :{
    id :number;
    params ?:{ [param :string] :string | string[] };
  }) :Observable<any> {
    const { id, params = {} } = data;

    return this.apiService.get(
      `${this.environment.urlV2}/location/locations/${id}`,
      { params },
    );
  }
}
